import { ComponentProps, FC, forwardRef, memo, PropsWithChildren } from "react";
import * as _ from "lodash";
import { ContainerConfig, Options } from "@ant-design/charts";
import { useDeepCompareEffect, useDeepCompareMemo } from "use-deep-compare";
import Markdown from "react-markdown";
import ReactJson from "react-json-view";

import {
  Alert,
  Button,
  Charts,
  Empty,
  EyeInvisibleOutlined,
  KpiLocked,
  KpiUnavailable,
  Space,
  Tag,
  Typography
} from "@ctra/components";

import {
  ChartData,
  ChartDataOptions,
  ChartEntity,
  ChartTimePeriod,
  ChartViewOptions,
  DataDescriptorEntity,
  Enterprise,
  ExtendedEventList
} from "@ctra/api";

import { defaultLocale, Enterprise as Content, useTranslation } from "@ctra/i18n";
import { classname, isDebugMode, Optional } from "@ctra/utils";
import { GAContext, useGoogleAnalytics } from "@ctra/analytics";
import { ChartContext } from "@ctra/charts";

import { useFarm } from "@farms";
import { useEventList } from "@events";
import { Routes } from "@routes";

import { SeriesDefinition, useChartAPI, useChartFilters } from "../../providers";
import { Toolbar } from "../Toolbar";
import { ChartExtra } from "../ChartExtra";
import { ChartView } from "../ChartView";
import { ChartFooter } from "../ChartFooter";

import styles from "./Chart.module.less";

const { Text } = Typography;
const { Chart: ChartUI } = Charts;

export type ChartProps = PropsWithChildren<{
  variant?: Charts.ChartVariant;
  viewOptions?: ChartViewOptions;
  dataOptions?: ChartDataOptions;
  config?: Partial<Pick<ContainerConfig, "onReady" | "onEvent"> & Options>;
  handleCompare?: () => void;
  handleClose?: () => void;
}>;

const {
  kpi: { displayName, noFarmSupport, noFarmConsent, singleFarmChart },
  chart: {
    beta,
    error: { title: errorTitle, description: errorDescription },
    empty: { description: emptyDescription, manualDescription, manualCta }
  }
} = Content;

/**
 * Lose equal function
 * @param self
 * @param other
 * @returns {boolean}
 */
const loseEqual = (self: unknown, other: unknown) =>
  _.isEqualWith(self, other, (a, b) => (_.isFunction(a) || _.isFunction(b) ? true : void 0));

/**
 * Chart UI wrapper with default labels
 * @param {string} variant
 * @param {Omit<React.PropsWithChildren<{variant?: React.ComponentProps<typeof Chart>["variant"]} & ChartProps & {children?: React.ReactNode | undefined}>, "variant">} props
 * @returns {JSX.Element}
 * @constructor
 */
const UIWrapper: FC<
  {
    variant?: ComponentProps<typeof Chart>["variant"];
  } & ComponentProps<typeof ChartUI>
> = ({ variant = Charts.ChartVariant.V3, ...props }) => {
  const { t } = useTranslation();
  const { charts } = useChartAPI();

  /**
   * Check if the chart is a beta chart
   * @type {boolean}
   */
  const isBetaChart = useDeepCompareMemo(() => {
    return _.some(charts, (chart) => {
      const {
        entity: {
          flags: { isBetaChart }
        }
      } = chart;

      return isBetaChart;
    });
  }, [charts]);

  /**
   * Make some UI props
   * @type {{subtitle: string, variant: ChartVariant | undefined, description: string, tag: any, title: any}}
   */
  const defaultProps: Partial<ComponentProps<typeof ChartUI>> = {
    variant,
    tag: isBetaChart ? <Tag className={styles.BetaTag}>{t<string>(beta)}</Tag> : void 0
  };

  return <ChartUI {...defaultProps} {...props} />;
};

/**
 * Graph component with error handling (empty components)
 * @param {{}} viewOptions
 * @param {boolean} hasConsent
 * @param {boolean} hasFailed
 * @param {boolean} isMultiFarmFilterSupported
 * @param {boolean} isEmpty
 * @param {boolean} isLoading
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @returns {JSX.Element}
 * @constructor
 */
const GraphWrapper: FC<{
  config: ChartProps["config"];
  viewOptions: ChartProps["viewOptions"];
  flags: {
    isMultiFarmFilterSupported: boolean;
    isLoading: boolean;
    hasConsent: boolean;
    hasFailed: boolean;
    isEmpty: boolean;
  };
}> = ({
  config: { height } = {},
  viewOptions = {},
  flags: { hasConsent, hasFailed, isMultiFarmFilterSupported, isEmpty, isLoading },
  children
}) => {
  const { t } = useTranslation();
  const { zoomed } = viewOptions;

  const {
    metricVariants,
    api: { fetchData }
  } = useChartAPI();

  /**
   * Check if any of the charts is a manual input chart
   * @type {boolean}
   */
  const hasManualInputInCollection = useDeepCompareMemo(
    () =>
      _.some(metricVariants, (variant) => {
        const {
          dataProperties: { sourceTypes }
        } = variant;

        return _.some(sourceTypes, (source) => _.startsWith(_.toLower(source), "manual"));
      }),
    [metricVariants]
  );

  return (
    <div
      className={classname(styles.Graph, zoomed ? styles.Zoom : null)}
      data-testid={"graph"}
      style={height ? { minHeight: height } : {}}
    >
      {!hasConsent &&
        (isMultiFarmFilterSupported ? (
          <Empty
            className={styles.Empty}
            image={<KpiLocked />}
            description={<Markdown>{t<string>(noFarmConsent)}</Markdown>}
          />
        ) : (
          <Empty
            data-testid="chart-error-single-farm"
            className={styles.Empty}
            image={<EyeInvisibleOutlined className={styles.Invisible} />}
            description={
              <Space className={styles.SingleFarmDescription} direction="vertical" size="middle">
                <Text>{t(singleFarmChart.title)}</Text>
                <Text type="secondary">
                  <Markdown>{t(singleFarmChart.description)}</Markdown>
                </Text>
              </Space>
            }
          />
        ))}
      {hasConsent && hasFailed && (
        <Alert
          data-testid={"chart-error"}
          className={styles.Error}
          message={t<string>(errorTitle)}
          /**
           * Showing the chart id in dev env helps for debugging
           */
          description={t<string>(errorDescription)}
          type="error"
          action={
            <Button size="small" danger onClick={fetchData}>
              Retry
            </Button>
          }
          showIcon
        />
      )}
      {hasConsent && !hasFailed && (
        <>
          {isEmpty ? (
            hasManualInputInCollection ? (
              <Empty
                className={styles.Empty}
                image={isLoading ? null : void 0}
                description={
                  isLoading ? (
                    ""
                  ) : (
                    <Space className={styles.SingleFarmDescription} direction="vertical" size="middle">
                      <Text>{t<string>(emptyDescription)}</Text>
                      <Text onClick={(evt) => evt.stopPropagation()} type="secondary">
                        <Markdown>{t<string>(manualDescription)}</Markdown>
                      </Text>
                      <Button
                        type="primary"
                        onClick={(evt) => {
                          evt.stopPropagation();
                          Enterprise.history.push(Routes.app.settings.inputs);
                        }}
                      >
                        {t<string>(manualCta)}
                      </Button>
                    </Space>
                  )
                }
              />
            ) : (
              <Empty
                className={styles.Empty}
                image={isLoading ? null : void 0}
                description={<Markdown>{isLoading ? "" : t<string>(emptyDescription)}</Markdown>}
              />
            )
          ) : (
            children
          )}
        </>
      )}
    </div>
  );
};

/**
 * Inner chart component with all the dependencies and API related to a chart
 * @param {Partial<Pick<ContainerConfig<Options, Plot<O>>, "onReady" | "onEvent"> & Options> | undefined} defaultConfig
 * @param {ChartVariant | undefined} variant
 * @param {{}} dataOptions
 * @param {{}} viewOptions
 * @param {(() => void) | undefined} handleCompare
 * @param {(() => void) | undefined} handleClose
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @returns {JSX.Element}
 * @constructor
 */
const Inner = forwardRef<
  unknown,
  PropsWithChildren<{
    eventHandlers: Pick<ChartProps, "handleCompare" | "handleClose">;
    variant: ChartProps["variant"];
    config: ChartProps["config"];
    options: Pick<ChartProps, "dataOptions" | "viewOptions">;
  }>
>(
  (
    {
      config: injectedConfig = {},
      variant = Charts.ChartVariant.V3,
      options: { dataOptions = {}, viewOptions = {} },
      eventHandlers: { handleCompare, handleClose },
      children
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { farm, site } = useFarm();
    const { data: injectedData, ...defaultConfig } = injectedConfig;

    const {
      api: { getSeries, setSeries },
      timePeriod,
      bucketSize
    } = useChartFilters();

    const {
      charts,
      metricVariants,
      meta: { isLoading: isChartDataLoading, hasFailed, hasConsent }
    } = useChartAPI();

    const {
      events,
      meta: { isLoading: isEventListLoading }
    } = useEventList<ExtendedEventList>();

    const series = _.reduce(
      metricVariants,
      (acc, { id }) => {
        const { available, active } = getSeries(id);

        return {
          available: {
            ...acc.available,
            ...available
          },
          active: _.uniq(_.compact([...acc.active, ...active]))
        };
      },
      {
        available: {},
        active: []
      } as SeriesDefinition
    );

    /**
     * Memoize the time period
     * @type {React.MutableRefObject<ChartTimePeriod | undefined>}
     */
    const timePeriodMemo = useDeepCompareMemo<ChartTimePeriod>(() => {
      return timePeriod;
    }, [timePeriod]);

    /**
     * Memoize the events for the chart
     * @type {Dictionary<ExtendedEventEntity<EventContext>>}
     */
    const eventCollectionMemo = useDeepCompareMemo(() => {
      return _.pickBy(events, ({ context }) => {
        if (context.chartResourceType === "variantId") {
          const variantId = context.chartResourceID;
          const [dataDescriptor] = metricVariants;

          return variantId === dataDescriptor.id;
        }

        return true;
      });
    }, [events, metricVariants]);

    /**
     * Collect chart data in a { [ChartEntity.id]: ChartData } format
     */
    const dataCollectionMemo = useDeepCompareMemo<
      Record<ChartEntity["id"], Optional<ChartData["data"]>>
    >(() => {
      return _.reduce(
        charts,
        (acc, { entity, data }) => {
          acc[entity.id] = data;

          return acc;
        },
        {} as Record<ChartEntity["id"], Optional<ChartData["data"]>>
      );
    }, [charts]);

    /**
     * Make a collection of charts views
     * @type {ReturnType<(chart: ChartEntity, metadata: MetaType) => InstanceType<RegisteredChartViews>>}
     */
    const viewCollectionMemo = useDeepCompareMemo<
      Record<ChartEntity["id"], ReturnType<typeof ChartView.create>>
    >(() => {
      return _.reduce(
        charts,
        (acc, { entity, meta }) => {
          if (!!meta && !_.isEmpty(meta)) {
            acc[entity.id] = ChartView.create(entity, meta);
          } else {
            acc[entity.id] = ChartView.createEmpty();
          }

          return acc;
        },
        {} as Record<ChartEntity["id"], ReturnType<typeof ChartView.create>>
      );
    }, [charts]);

    /**
     * Collect chart meta in a { [ChartEntity.id]: MetaType } format using the chart view constructor
     * @type {Record<ChartEntity["id"], Optional<ChartData["meta"]>>}
     */
    const metaCollectionMemo = useDeepCompareMemo<Record<ChartEntity["id"], ChartData["meta"]>>(() => {
      return _.reduce(
        charts,
        (acc, { entity }) => {
          const meta = viewCollectionMemo[entity.id].getMetadata();

          if (meta) {
            acc[entity.id] = viewCollectionMemo[entity.id].getMetadata();
          }

          return acc;
        },
        {} as Record<ChartEntity["id"], ChartData["meta"]>
      );
    }, [charts, viewCollectionMemo]);

    /**
     * Tell if the farm filter is supported: all charts in the collection must match
     * @type {boolean}
     */
    const isMultiFarmFilterSupported = useDeepCompareMemo<boolean>(
      () => _.every(charts, (chart) => _.get(chart, ["flags", "isMultiFarmFilterSupported"], false)),
      [charts]
    );

    /**
     * Chart context props
     * @type {React.ComponentProps<React.ForwardRefExoticComponent<React.PropsWithoutRef<React.PropsWithChildren<ChartProviderProps>> & React.RefAttributes<unknown>>>}
     */
    const ctxProps = useDeepCompareMemo<ComponentProps<typeof ChartContext.Provider>>(() => {
      const entities = _.map(charts, "entity");

      const visibleSeries = _.isEmpty(series.active)
        ? series.available
        : _.pick(series.available, series.active);

      return {
        entities,
        viewOptions,
        series: visibleSeries,
        data: dataCollectionMemo,
        meta: metaCollectionMemo,
        events: eventCollectionMemo,
        dataOptions: { ...dataOptions, timePeriod: timePeriodMemo, bucketSize },
        config: defaultConfig,
        children: []
      };
    }, [
      farm?.id,
      site?.id,
      charts,
      defaultConfig,
      viewOptions,
      dataOptions,
      timePeriodMemo,
      bucketSize,
      dataCollectionMemo,
      metaCollectionMemo,
      eventCollectionMemo,
      series
    ]);

    /**
     * Get the graph component from the main chart view
     */
    const GraphComponent = useDeepCompareMemo(() => {
      const [main] = charts;

      if (!main) {
        throw new Error("No chart found, why?");
      }

      const { entity } = main;
      const view = viewCollectionMemo[entity.id];

      return view.getComponent();
    }, [charts, viewCollectionMemo]);

    /**
     * Tell if any of the dependencies are loading
     * @type {boolean}
     */
    const isDataLoading = _.some(
      _.some(viewOptions.showEvents) ? [isChartDataLoading, isEventListLoading] : [isChartDataLoading]
    );

    /**
     * Update the legends as the meta changes
     */
    useDeepCompareEffect(() => {
      const variantIDCollection = _.map(metaCollectionMemo, "variantId");

      /**
       * All the series from the filter context
       * @type {Record<string, SeriesDefinition>}
       */
      const series = _.reduce(
        variantIDCollection,
        (acc, variantId) => {
          acc[variantId] = getSeries(variantId);

          return acc;
        },
        {} as Record<DataDescriptorEntity["id"], SeriesDefinition>
      );

      /**
       * Get the available series from the meta (in other words, legend values)
       * @type {Record<string, Record<string, string>>}
       */
      const legendValues = _.reduce(
        metaCollectionMemo,
        (acc, meta) => {
          acc[meta.variantId] = _.get(meta, ["series", "keys"], {});

          return acc;
        },
        {} as Record<DataDescriptorEntity["id"], SeriesDefinition["available"]>
      );

      if (!loseEqual(legendValues, _.mapValues(series, "available"))) {
        _.map(variantIDCollection, (variantID) => {
          setSeries(variantID, {
            available: legendValues[variantID],
            active: _.intersection(series[variantID].active, _.keys(legendValues[variantID]))
          });
        });
      }
    }, [metaCollectionMemo]);

    return (
      <ChartContext.Provider {...ctxProps} ref={ref}>
        <UIWrapper
          variant={variant}
          loading={isDataLoading}
          tip={t<string>(Content.chart.loading)}
          extension={children}
          filters={viewOptions.showToolbar && <Toolbar />}
          extra={
            <ChartExtra
              viewOptions={viewOptions}
              handleCompare={handleCompare}
              handleClose={handleClose}
              variant={variant}
              config={defaultConfig}
            />
          }
          footer={variant !== Charts.ChartVariant.V3 ? <ChartFooter /> : null}
        >
          <GraphWrapper
            viewOptions={viewOptions}
            config={injectedConfig}
            flags={{
              isLoading: isChartDataLoading,
              hasConsent,
              hasFailed,
              isMultiFarmFilterSupported,
              isEmpty: _.every(dataCollectionMemo, _.isEmpty)
            }}
          >
            <GraphComponent />
            {isDebugMode() ? (
              <div onClick={(e) => e.stopPropagation()}>
                <ReactJson
                  style={{
                    border: "1px dashed #ccc",
                    padding: 16,
                    margin: "32px 0 0 0",
                    maxHeight: 300,
                    overflow: "scroll"
                  }}
                  src={_.omit(ctxProps, "data", "events")}
                />
              </div>
            ) : null}
          </GraphWrapper>
        </UIWrapper>
      </ChartContext.Provider>
    );
  }
);

Inner.displayName = "Inner";

/**
 * Chart component
 * @param {ChartVariant | undefined} variant
 * @param {{}} viewOptions
 * @param {{}} dataOptions
 * @param {{}} defaultConfig
 * @param {(() => void) | undefined} handleCompare
 * @param {(() => void) | undefined} handleClose
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @returns {JSX.Element}
 * @constructor
 */
const ChartWithForwardedRef = forwardRef<unknown, ChartProps>(
  (
    {
      variant = Charts.ChartVariant.V3,
      viewOptions = {},
      dataOptions = {},
      config = {},
      handleCompare,
      handleClose,
      children
    },
    ref
  ) => {
    const { category } = useGoogleAnalytics();
    const { t } = useTranslation();
    const { skipFarmSupportCheck } = viewOptions;

    const {
      metricVariants,
      metricVariants: [
        {
          dataProperties: { typeName }
        }
      ],
      meta: {
        farms: { hasSupport }
      }
    } = useChartAPI();

    return (
      <GAContext.Provider
        value={{
          category,
          label: t<string>(displayName(typeName), {
            case: null,
            variant: null,
            makeDefaultValue: true,
            lng: defaultLocale,
            collectionSize: _.size(metricVariants)
          })
        }}
      >
        {!skipFarmSupportCheck && _.isEmpty(hasSupport) ? (
          <UIWrapper extra={<ChartExtra viewOptions={viewOptions} handleClose={handleClose} />}>
            <Empty
              className={styles.Empty}
              image={<KpiUnavailable />}
              description={<Markdown>{t<string>(noFarmSupport)}</Markdown>}
            />
          </UIWrapper>
        ) : (
          <Inner
            ref={ref}
            config={config}
            variant={variant}
            eventHandlers={{ handleCompare, handleClose }}
            options={{
              dataOptions,
              viewOptions
            }}
          >
            {children}
          </Inner>
        )}
      </GAContext.Provider>
    );
  }
);

ChartWithForwardedRef.displayName = "Chart";

const Chart = memo(ChartWithForwardedRef, (prevProps, nextProps) => {
  return _.isEqualWith(prevProps, nextProps, (objValue, othValue) => {
    if (_.isFunction(objValue) || _.isFunction(othValue)) {
      return true;
    }
  });
});

export { Chart };
